/**
 * PatientPortalView.js
 * Patient-facing portal — login with access code + PIN, then view personal health records.
 * No staff session required. Uses unauthenticated IPC: portal:login, portal:get-data.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Shield, LogOut, User, Calendar, Pill, FlaskConical,
  AlertTriangle, Activity, ChevronDown, ChevronUp, ArrowLeft,
  Heart, Thermometer, Wind, Clock,
} from 'lucide-react';
import { NEU } from '../neuStyles';
import PortalPaymentSection from '../components/PortalPaymentSection';
import PortalReminderModal from '../components/PortalReminderModal';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h);
  return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
}

function formatAppointmentType(type) {
  return (type || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const SEVERITY_COLOR = {
  mild:   { bg: '#fef9c3', text: '#854d0e' },
  moderate: { bg: '#ffedd5', text: '#9a3412' },
  severe: { bg: '#fee2e2', text: '#991b1b' },
};

// ─── Section card wrapper ─────────────────────────────────────────────────────

function Section({ icon, title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ ...NEU.card, marginBottom: '1rem', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700, fontSize: '1rem', color: '#111827' }}>
          {icon}{title}
        </span>
        {open ? <ChevronUp size={18} color="#6C757D" /> : <ChevronDown size={18} color="#6C757D" />}
      </button>
      {open && (
        <div style={{ padding: '0 1.25rem 1.25rem' }}>
          {children}
        </div>
      )}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <p style={{ color: '#6C757D', fontSize: '0.875rem', fontStyle: 'italic', margin: 0 }}>{text}</p>
  );
}

// ─── Login Screen ─────────────────────────────────────────────────────────────

function PortalLogin({ onLoginSuccess }) {
  const [accessCode, setAccessCode] = useState('');
  const [pin, setPin]               = useState('');
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!accessCode.trim() || !pin.trim()) {
      setError('Please enter both your access code and PIN.');
      return;
    }
    setLoading(true);
    try {
      const result = await window.zignagenixAPI.portalLogin(accessCode.trim().toUpperCase(), pin.trim());
      if (result?.success) {
        onLoginSuccess(result);
      } else {
        setError(result?.message || 'Invalid access code or PIN. Please check your credentials and try again.');
      }
    } catch (err) {
      console.error('Portal login error:', err);
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ ...NEU.cardLg, padding: '2.5rem', width: '100%', maxWidth: '420px' }}>
        {/* Logo / Brand */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '50%', background: '#dbeafe', marginBottom: '1rem' }}>
            <Shield size={32} color="#2563eb" />
          </div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: '#111827' }}>Patient Portal</h1>
          <p style={{ margin: '0.5rem 0 0', color: '#6C757D', fontSize: '0.875rem' }}>ZignaGenix Medical Intelligence Platform</p>
        </div>

        {/* Instructions */}
        <div style={{ ...NEU.cardSm, background: '#f0fdf4', padding: '0.875rem', marginBottom: '1.5rem' }}>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#166534', lineHeight: 1.5 }}>
            <strong>Access your health records securely.</strong> Use the access code and PIN provided by your healthcare provider. Contact your clinic if you need assistance.
          </p>
        </div>

        {error && (
          <div style={{ ...NEU.cardSm, background: '#fee2e2', padding: '0.75rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <AlertTriangle size={16} color="#DC3545" />
            <span style={{ color: '#DC3545', fontSize: '0.875rem' }}>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', color: '#374151', marginBottom: '0.4rem' }}>
              Access Code
            </label>
            <input
              type="text"
              value={accessCode}
              onChange={e => setAccessCode(e.target.value.toUpperCase())}
              placeholder="e.g. A1B2C3D4E5F6"
              maxLength={20}
              style={{
                ...NEU.input,
                width: '100%', padding: '0.625rem 0.875rem', fontSize: '1rem', letterSpacing: '0.1em',
                fontFamily: 'monospace', boxSizing: 'border-box',
              }}
              onFocus={e => e.target.style.boxShadow = NEU.shadow.sm}
              onBlur={e => e.target.style.boxShadow = NEU.shadow.insetSm}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', color: '#374151', marginBottom: '0.4rem' }}>
              PIN
            </label>
            <input
              type="password"
              value={pin}
              // H3 (audit Apr 26 2026): widened from 4 → 8 chars. Old patients
              // with 4-digit PINs still authenticate (server hashes whatever
              // is submitted); new patients get 8-digit PINs.
              onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="PIN"
              maxLength={8}
              style={{
                ...NEU.input,
                width: '100%', padding: '0.625rem 0.875rem', fontSize: '1.25rem', letterSpacing: '0.3em',
                boxSizing: 'border-box',
              }}
              onFocus={e => e.target.style.boxShadow = NEU.shadow.sm}
              onBlur={e => e.target.style.boxShadow = NEU.shadow.insetSm}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '0.75rem', background: loading ? '#93c5fd' : '#2563eb',
              color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700,
              fontSize: '1rem', cursor: loading ? 'not-allowed' : 'pointer', transition: 'background 0.15s',
            }}
          >
            {loading ? 'Signing in…' : 'Access My Records'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.75rem', color: '#9ca3af', lineHeight: 1.6 }}>
          This portal contains protected health information.<br />Your session is private and secure.
        </p>
      </div>
    </div>
  );
}

// ─── Portal Dashboard ─────────────────────────────────────────────────────────

function PortalLogoutModal({ onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.45)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 10000,
    }} onClick={onCancel}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: '2rem',
        width: '100%', maxWidth: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        textAlign: 'center',
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%', background: '#fef2f2',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 1rem',
        }}>
          <LogOut size={24} color="#DC3545" />
        </div>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', color: '#111827' }}>
          Confirm Sign Out
        </h3>
        <p style={{ margin: '0 0 1.5rem', color: '#6C757D', fontSize: '0.875rem', lineHeight: 1.5 }}>
          Are you sure you want to sign out of the Patient Portal?
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
          <button onClick={onCancel} style={{
            padding: '0.5rem 1.25rem', border: '1px solid #dee2e6',
            borderRadius: 8, background: '#fff', color: '#374151',
            fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            padding: '0.5rem 1.25rem', border: 'none',
            borderRadius: 8, background: '#DC3545', color: '#fff',
            fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
          }}>Sign Out</button>
        </div>
      </div>
    </div>
  );
}

/**
 * P113 — the session-ending warning.
 *
 * A patient session is an ABSOLUTE 30-minute cap from sign-in, enforced
 * server-side on every call. Nothing extends it, so there is deliberately NO
 * "Stay signed in" button here: a cap any button can postpone is not a cap.
 * The dialog exists so the end is not a surprise — [OK] dismisses the notice,
 * it does not buy time, and the wording says so.
 */
function PortalExpiryWarning({ msLeft, onSignOutNow, onDismiss }) {
  const okRef = useRef(null);
  useEffect(() => { okRef.current?.focus(); }, []);

  const total = Math.max(0, Math.ceil(msLeft / 1000));
  const clock = `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem', zIndex: 9999,
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="portal-expiry-title"
        aria-describedby="portal-expiry-body"
        style={{ ...NEU.card, padding: '1.75rem', maxWidth: 420, width: '100%', textAlign: 'center' }}
      >
        <Clock size={34} color="#D97706" style={{ marginBottom: '0.75rem' }} />
        <h2 id="portal-expiry-title" style={{ margin: '0 0 0.6rem', fontSize: '1.05rem', color: '#111827' }}>
          Your session is about to end
        </h2>
        <p id="portal-expiry-body" style={{ color: '#374151', lineHeight: 1.6, margin: '0 0 1.25rem', fontSize: '0.9rem' }}>
          For your security, this session ends in{' '}
          <strong aria-live="polite" aria-atomic="true" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {clock}
          </strong>
          . Your information will be cleared from the screen.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
          <button
            ref={okRef}
            type="button"
            className="btn-primary"
            onClick={onDismiss}
            style={{ padding: '0.55rem 1.4rem' }}
          >
            OK
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={onSignOutNow}
            style={{
              padding: '0.55rem 1.4rem', border: '1px solid #d1d5db',
              borderRadius: 8, background: '#fff', color: '#374151',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <LogOut size={14} /> Sign out now
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * P113 — what a patient sees once the cap is reached. Deliberately a SEPARATE
 * screen from the generic "Session Error": that one offers "Try Again", which
 * is wrong here. The session is gone and only the access code can start a new
 * one, so the copy says exactly that and nothing else.
 */
function PortalSessionEnded({ onNavigate }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ ...NEU.card, padding: '2rem', maxWidth: 420, textAlign: 'center' }}>
        <Shield size={38} color="#2563eb" style={{ marginBottom: '1rem', opacity: 0.8 }} />
        <h2 style={{ margin: '0 0 0.75rem', color: '#111827' }}>Your session ended</h2>
        <p style={{ color: '#6C757D', marginBottom: onNavigate ? '1.5rem' : 0, lineHeight: 1.6 }}>
          Sign in again with your access code and PIN.
        </p>
        {onNavigate && (
          <button className="btn-primary" onClick={() => onNavigate('dashboard')} style={{ padding: '0.55rem 1.4rem' }}>
            Back to sign in
          </button>
        )}
      </div>
    </div>
  );
}

function PortalDashboard({ session, data, onLogout, sessionToken }) {
  const { patient, medications, labs, allergies, appointments, vitals, care_summary } = data;
  const fullName = `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Phase 5e (Jun 11 2026): patient-portal popup for due follow-up
          reminders. Token-gated server-side; renders nothing when none due. */}
      <PortalReminderModal sessionToken={sessionToken} />
      {/* Top bar */}
      <div style={{ background: '#F8F9FA', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', padding: '0.875rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Shield size={22} color="#2563eb" />
          <span style={{ fontWeight: 700, color: '#111827', fontSize: '1rem' }}>Patient Portal</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '0.875rem', color: '#374151' }}>
            Welcome, <strong>{session.patientName || fullName}</strong>
          </span>
          <button
            onClick={() => setShowLogoutConfirm(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              padding: '0.4rem 0.875rem', ...NEU.btnGhost,
              borderRadius: '6px', fontSize: '0.8rem',
            }}
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </div>
      {showLogoutConfirm && (
        <PortalLogoutModal
          onConfirm={onLogout}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}

      {/* Content */}
      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '1.5rem 1rem' }}>
        {/* Personal Info */}
        <Section icon={<User size={18} color="#2563eb" />} title="My Information">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
            {[
              { label: 'Full Name',      value: fullName },
              { label: 'Date of Birth',  value: formatDate(patient?.date_of_birth) },
              { label: 'Gender',         value: patient?.gender === 'M' ? 'Male' : patient?.gender === 'F' ? 'Female' : patient?.gender === 'O' ? 'Other' : '—' },
              { label: 'Phone',          value: patient?.phone || '—' },
              { label: 'Email',          value: patient?.email || '—' },
            ].map(({ label, value }) => (
              <div key={label}>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#6C757D', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>{label}</p>
                <p style={{ margin: 0, fontWeight: 600, color: '#111827', fontSize: '0.9rem' }}>{value}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* My Treatment & Care (Phase E — curated, patient-facing) */}
        {care_summary && (
          <Section icon={<Activity size={18} color="#2E8B98" />} title="My Treatment & Care">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {(care_summary.clinic || care_summary.procedure_scheduled_at) && (
                <div style={{ ...NEU.cardSm, padding: '0.875rem', background: '#f0fdfa' }}>
                  {care_summary.clinic && <p style={{ margin: 0, fontWeight: 600, color: '#111827', fontSize: '0.9rem' }}>Clinic: {care_summary.clinic}</p>}
                  {care_summary.procedure_scheduled_at && <p style={{ margin: '2px 0 0', color: '#047857', fontSize: '0.85rem' }}>Procedure date: {formatDate(care_summary.procedure_scheduled_at)}</p>}
                </div>
              )}

              <div>
                <p style={{ margin: '0 0 4px', fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>Your therapies</p>
                {!care_summary.therapies?.length ? (
                  <EmptyState text="No therapies recorded yet." />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {care_summary.therapies.map((t, i) => (
                      <div key={i} style={{ ...NEU.cardSm, padding: '0.7rem 0.875rem', display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
                        <span style={{ color: '#111827', fontSize: '0.875rem' }}>{t.therapy}{t.route ? ` · ${t.route}` : ''}</span>
                        <span style={{ color: '#6C757D', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{t.date ? formatDate(t.date) : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {care_summary.follow_ups?.length > 0 && (
                <div>
                  <p style={{ margin: '0 0 4px', fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>Follow-up schedule</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {care_summary.follow_ups.map((f, i) => (
                      <span key={i} style={{ fontSize: '0.78rem', padding: '3px 10px', borderRadius: '999px', border: '1px solid #c9d6e8', background: f.done ? '#dcfce7' : '#fff', color: f.done ? '#166534' : '#374151' }}>
                        {f.marker} · {f.due_at ? formatDate(f.due_at) : ''}{f.done ? ' ✓' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {care_summary.consents_signed?.length > 0 && (
                <p style={{ margin: 0, color: '#047857', fontSize: '0.82rem' }}>
                  ✓ Consents on file: {care_summary.consents_signed.map((r) => r === 'physician' ? 'physician' : 'patient').join(', ')}
                </p>
              )}
            </div>
          </Section>
        )}

        {/* Payments */}
        <PortalPaymentSection sessionToken={sessionToken} />

        {/* Upcoming Appointments */}
        <Section icon={<Calendar size={18} color="#4FA038" />} title={`Upcoming Appointments (${appointments?.length || 0})`}>
          {!appointments?.length ? (
            <EmptyState text="No upcoming appointments on file." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {appointments.map((appt, i) => (
                <div key={i} style={{ ...NEU.cardSm, display: 'flex', gap: '1rem', alignItems: 'flex-start', padding: '0.875rem', background: '#f0fdf4' }}>
                  <div style={{ minWidth: '90px', textAlign: 'center' }}>
                    <p style={{ margin: 0, fontWeight: 700, color: '#065f46', fontSize: '0.875rem' }}>{formatDate(appt.appointment_date)}</p>
                    <p style={{ margin: 0, color: '#047857', fontSize: '0.8rem' }}>{formatTime(appt.start_time)}</p>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontWeight: 600, color: '#111827', fontSize: '0.9rem' }}>{formatAppointmentType(appt.appointment_type)}</p>
                    <p style={{ margin: 0, color: '#6C757D', fontSize: '0.8rem' }}>Provider: {appt.provider_name || 'To be confirmed'}</p>
                  </div>
                  <span style={{ marginLeft: 'auto', fontSize: '0.75rem', padding: '2px 10px', borderRadius: '999px', background: '#dcfce7', color: '#166534', fontWeight: 600, whiteSpace: 'nowrap', alignSelf: 'center' }}>
                    {appt.status?.replace(/_/g, ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Medications */}
        <Section icon={<Pill size={18} color="#2E8B98" />} title={`Current Medications (${medications?.length || 0})`}>
          {!medications?.length ? (
            <EmptyState text="No active medications on file." />
          ) : (
            <div className="responsive-scroll" style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', minWidth: 640 }}>
                <thead>
                  <tr style={{ ...NEU.th }}>
                    {['Medication', 'Dosage', 'Frequency', 'Route', 'Start Date'].map(h => (
                      <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {medications.map((med, i) => (
                    <tr key={i} style={{ ...NEU.td, background: i % 2 === 0 ? '#F8F9FA' : '#fafafa' }}>
                      <td style={{ padding: '0.625rem 0.75rem', fontWeight: 600, color: '#111827' }}>{med.drug_name}</td>
                      <td style={{ padding: '0.625rem 0.75rem', color: '#374151' }}>{med.dosage || '—'}</td>
                      <td style={{ padding: '0.625rem 0.75rem', color: '#374151' }}>{med.frequency || '—'}</td>
                      <td style={{ padding: '0.625rem 0.75rem', color: '#374151' }}>{med.route || '—'}</td>
                      <td style={{ padding: '0.625rem 0.75rem', color: '#374151' }}>{formatDate(med.start_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* Lab Results */}
        <Section icon={<FlaskConical size={18} color="#DC3545" />} title={`Recent Lab Results (${labs?.length || 0})`}>
          {!labs?.length ? (
            <EmptyState text="No finalized lab results on file." />
          ) : (
            <div className="responsive-scroll" style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', minWidth: 540 }}>
                <thead>
                  <tr style={{ ...NEU.th }}>
                    {['Test', 'Result', 'Reference Range', 'Date'].map(h => (
                      <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {labs.map((lab, i) => (
                    <tr key={i} style={{ ...NEU.td, background: lab.is_abnormal ? '#fff7ed' : (i % 2 === 0 ? '#F8F9FA' : '#fafafa') }}>
                      <td style={{ padding: '0.625rem 0.75rem', fontWeight: 600, color: '#111827' }}>{lab.test_name}</td>
                      <td style={{ padding: '0.625rem 0.75rem' }}>
                        <span style={{ color: lab.is_abnormal ? '#DC3545' : '#111827', fontWeight: lab.is_abnormal ? 700 : 400 }}>
                          {lab.result_value} {lab.result_unit}
                        </span>
                        {lab.is_abnormal && <span style={{ marginLeft: '6px', fontSize: '0.7rem', background: '#fee2e2', color: '#DC3545', padding: '1px 6px', borderRadius: '999px', fontWeight: 700 }}>Abnormal</span>}
                      </td>
                      <td style={{ padding: '0.625rem 0.75rem', color: '#6C757D', fontSize: '0.8rem' }}>{lab.reference_range || '—'}</td>
                      <td style={{ padding: '0.625rem 0.75rem', color: '#374151' }}>{formatDate(lab.specimen_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ margin: '0.75rem 0 0', fontSize: '0.75rem', color: '#6C757D' }}>Showing most recent 20 finalized results. Contact your provider to discuss any results.</p>
            </div>
          )}
        </Section>

        {/* Allergies */}
        <Section icon={<AlertTriangle size={18} color="#d97706" />} title={`Allergies (${allergies?.length || 0})`}>
          {!allergies?.length ? (
            <EmptyState text="No known allergies on file." />
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
              {allergies.map((a, i) => {
                const col = SEVERITY_COLOR[a.severity?.toLowerCase()] || { bg: '#f3f4f6', text: '#374151' };
                return (
                  <div key={i} style={{ padding: '0.625rem 1rem', borderRadius: '8px', background: col.bg, color: col.text, border: `1px solid ${col.text}30` }}>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: '0.875rem' }}>{a.allergen}</p>
                    {a.reaction && <p style={{ margin: '2px 0 0', fontSize: '0.75rem', opacity: 0.85 }}>{a.reaction}</p>}
                    {a.severity && <span style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'capitalize' }}>{a.severity}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* Vitals */}
        <Section icon={<Activity size={18} color="#2E8B98" />} title={`Recent Vitals (${vitals?.length || 0} entries)`} defaultOpen={false}>
          {!vitals?.length ? (
            <EmptyState text="No vitals recorded." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {vitals.map((v, i) => (
                <div key={i} style={{ ...NEU.cardSm, padding: '0.875rem', background: '#f0f9ff' }}>
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.75rem', color: '#0c4a6e', fontWeight: 600 }}>{formatDate(v.recorded_at)}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1.5rem', fontSize: '0.85rem' }}>
                    {v.blood_pressure_sys && v.blood_pressure_dia && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Heart size={13} color="#0284c7" />
                        <strong>{v.blood_pressure_sys}/{v.blood_pressure_dia}</strong> mmHg
                      </span>
                    )}
                    {v.heart_rate && (
                      <span><strong>{v.heart_rate}</strong> bpm</span>
                    )}
                    {v.temperature_f && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Thermometer size={13} color="#0284c7" />
                        <strong>{v.temperature_f}</strong>°F
                      </span>
                    )}
                    {v.oxygen_saturation && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Wind size={13} color="#0284c7" />
                        <strong>{v.oxygen_saturation}</strong>% O₂
                      </span>
                    )}
                    {v.weight_lbs && (
                      <span><strong>{v.weight_lbs}</strong> lbs</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '1.5rem 0 0.5rem', borderTop: 'none', boxShadow: 'inset 0 1px 0 rgba(186,199,216,0.45)', marginTop: '1rem' }}>
          <p style={{ margin: 0, fontSize: '0.75rem', color: '#9ca3af', lineHeight: 1.6 }}>
            This portal displays your health information securely. For medical emergencies, call 911.<br />
            Questions about your records? Contact your clinic directly.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PatientPortalView({ onNavigate, portalSessionProp, portalDataProp }) {
  const [portalSession, setPortalSession] = useState(portalSessionProp || null);
  const [portalData, setPortalData]       = useState(portalDataProp || null);
  const [loadingData, setLoadingData]     = useState(false);
  const [dataError, setDataError]         = useState('');
  // P113 — session-end state.
  const [policy, setPolicy]               = useState(null);   // { warnBeforeMs, ... }
  const [msLeft, setMsLeft]               = useState(null);   // ms to the SERVER's expiry
  const [warnDismissed, setWarnDismissed] = useState(false);
  const [sessionEnded, setSessionEnded]   = useState(false);

  // The server's own cut-off, preferred from the freshest response that
  // carries it: portal:get-data (every load) over portal:login (once).
  // ⚠ NULL on the staff-preview path, which mints no session — no countdown.
  const expiresAtMs = (() => {
    const raw = portalData?.sessionExpiresAt || portalSession?.sessionExpiresAt || null;
    if (!raw) return null;
    const t = new Date(raw).getTime();
    return Number.isFinite(t) ? t : null;
  })();

  // ⛔ THE ONE PLACE PHI LEAVES THE SCREEN AND MEMORY. Both state slots are
  // nulled, not just the one that renders — portalData holds the chart and
  // portalSession holds the token that could fetch it again.
  const endSession = useCallback(() => {
    setPortalSession(null);
    setPortalData(null);
    setDataError('');
    setMsLeft(null);
    setWarnDismissed(false);
    setSessionEnded(true);
  }, []);

  // (4) The SERVER is the authority. Any portal:* call answering EXPIRED
  // clears the screen, whichever call discovers it first — not only the one
  // this component happens to make.
  useEffect(() => {
    const off = window.zignagenixAPI?.onPortalSessionExpired?.(endSession);
    return () => { if (typeof off === 'function') off(); };
  }, [endSession]);

  // The patient session policy (warn lead). Separate from staff, by design.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await window.zignagenixAPI?.portalPolicy?.();
        if (!cancelled && p && !p.error) setPolicy(p);
      } catch { /* no policy ⇒ no warning; the server still ends the session */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Countdown to the SERVER's expiry — an absolute instant, not an interval
  // this component started. A tab that was backgrounded resumes with the real
  // time remaining, and a clock the server already moved past reads 0 at once.
  useEffect(() => {
    if (!expiresAtMs || !portalSession) { setMsLeft(null); return undefined; }
    const tick = () => setMsLeft(Math.max(0, expiresAtMs - Date.now()));
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [expiresAtMs, portalSession]);

  // At zero: clear. ⚠ `msLeft !== null` is load-bearing — the initial state is
  // null and a `<= 0` test against it would end the session on first render.
  useEffect(() => {
    if (portalSession && msLeft !== null && msLeft <= 0) endSession();
  }, [portalSession, msLeft, endSession]);

  async function handleLoginSuccess(session) {
    setPortalSession(session);
    setLoadingData(true);
    setDataError('');
    setSessionEnded(false);
    setWarnDismissed(false);
    try {
      // C2 (audit Apr 26 2026): use opaque sessionToken (was: integer tokenId)
      const data = await window.zignagenixAPI.portalGetData(session.sessionToken);
      if (data?.error) {
        setDataError(data.message || 'Failed to load your records. Please sign in again.');
        setPortalSession(null);
      } else {
        setPortalData(data);
      }
    } catch (err) {
      console.error('Portal data error:', err);
      setDataError('Unable to load records. Please try again.');
      setPortalSession(null);
    } finally {
      setLoadingData(false);
    }
  }

  function handleLogout() {
    setPortalSession(null);
    setPortalData(null);
    setDataError('');
    setMsLeft(null);
    setWarnDismissed(false);
    // If opened from login page portal mode, navigate back to login
    if (portalSessionProp && onNavigate) {
      onNavigate('dashboard');
    }
  }

  // Loading state
  if (loadingData) {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem' }}>
        <Shield size={40} color="#2563eb" style={{ opacity: 0.5 }} />
        <p style={{ color: '#6C757D', fontWeight: 600 }}>Loading your health records securely…</p>
      </div>
    );
  }

  // Error state
  if (dataError) {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ ...NEU.card, padding: '2rem', maxWidth: '400px', textAlign: 'center' }}>
          <AlertTriangle size={40} color="#DC3545" style={{ marginBottom: '1rem' }} />
          <h2 style={{ margin: '0 0 0.75rem', color: '#111827' }}>Session Error</h2>
          <p style={{ color: '#6C757D', marginBottom: '1.5rem' }}>{dataError}</p>
          <button className="btn-primary" onClick={() => setDataError('')}>Try Again</button>
          {onNavigate && (
            <button
              className="btn-ghost btn-sm"
              onClick={() => onNavigate('dashboard')}
              style={{ display: 'flex', alignItems: 'center', gap: '5px', margin: '0.75rem auto 0' }}
            >
              <ArrowLeft size={14} /> Back to Dashboard
            </button>
          )}
        </div>
      </div>
    );
  }

  // P113 — the cap was reached (or the server said EXPIRED). PHI is already
  // out of state by the time this renders; this screen only explains it.
  if (sessionEnded && !portalSession) {
    return <PortalSessionEnded onNavigate={portalSessionProp ? onNavigate : null} />;
  }

  // Logged in — show dashboard
  if (portalSession && portalData) {
    // Warn once the remaining time crosses the server-derived lead, unless the
    // patient has already acknowledged it. No "Stay signed in": the cap is
    // absolute and nothing on this screen can move it.
    const showWarning =
      !warnDismissed &&
      policy && typeof policy.warnBeforeMs === 'number' &&
      msLeft !== null && msLeft > 0 && msLeft <= policy.warnBeforeMs;

    return (
      <>
        <PortalDashboard session={portalSession} data={portalData} onLogout={handleLogout} sessionToken={portalSession.sessionToken} />
        {showWarning && (
          <PortalExpiryWarning
            msLeft={msLeft}
            onSignOutNow={handleLogout}
            onDismiss={() => setWarnDismissed(true)}
          />
        )}
      </>
    );
  }

  // Not logged in — show login
  return <PortalLogin onLoginSuccess={handleLoginSuccess} />;
}
